/*********************************************************************************************
 * 🎛️ PRODUCT DROPDOWN — listes déroulantes de la fiche produit et de la mini card
 * Options en liens (couleur, métal, accroche, charms) ou en boutons qui pilotent un select caché :
 * celui du VariantSelector (fiche produit) ou celui de la carte produit (mini card).
 * Délégation sur document : rien à rebrancher après un rechargement ou un swap de carte.
 * Chargé partout (mm-scripts, defer) : les cartes produit sont sur toutes les pages.
 */
const ProductDropdownManager = {
	config: {
		selectors: {
			dropdown: '[data-product-dropdown]',
			trigger: '[data-product-dropdown-trigger]',
			list: '[data-product-dropdown-list]',
			item: '[data-product-dropdown-item]',
			label: '[data-product-dropdown-label]',
			pickerContainer: '[data-variant-picker-container]',
			hiddenSelect: '[data-variant-input], [data-card-variant-input]',
			// Champ porté par le dropdown lui-même (sélecteur de devise du footer)
			ownInput: '[data-product-dropdown-input]',
		},
		classes: {
			open: 'mm-product-dropdown--open',
		},
	},

	init() {
		// Pas de garde sur la présence d'une liste : des cartes peuvent arriver après coup (filtres, pagination)
		this._bindEvents();
		window.log('info', 'Product', 'ProductDropdownManager initialized');
	},

	_bindEvents() {
		const { selectors } = this.config;

		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(selectors.trigger);
			if (trigger) {
				this.toggle(trigger.closest(selectors.dropdown));
				return;
			}

			const valueItem = e.target.closest(`${selectors.item}[data-product-dropdown-value]`);
			if (valueItem) {
				this._selectValue(valueItem);
			}
		});

		// Fermeture au toucher/clic extérieur : pointerdown, car iOS Safari n'envoie pas de click
		// au document quand on touche une zone non cliquable (texte, image)
		document.addEventListener('pointerdown', (e) => {
			if (!e.target.closest(selectors.dropdown)) this.closeAll();
		});

		document.addEventListener('keydown', (e) => {
			const dropdown = e.target.closest?.(selectors.dropdown);
			if (!dropdown) return;
			this._handleKeydown(e, dropdown);
		});

		// Tab hors de la liste : on la referme. focusin plutôt que focusout, qui sous Safari
		// (pas de focus au clic sur un bouton) fermerait la liste avant le clic sur une option.
		document.addEventListener('focusin', (e) => {
			document.querySelectorAll(`.${this.config.classes.open}`).forEach(dropdown => {
				if (!dropdown.contains(e.target)) this.close(dropdown);
			});
		});
	},

	toggle(dropdown) {
		if (!dropdown) return;
		dropdown.classList.contains(this.config.classes.open) ? this.close(dropdown) : this.open(dropdown);
	},

	open(dropdown) {
		this.closeAll();
		dropdown.classList.add(this.config.classes.open);
		dropdown.querySelector(this.config.selectors.trigger)?.setAttribute('aria-expanded', 'true');
	},

	close(dropdown) {
		dropdown.classList.remove(this.config.classes.open);
		dropdown.querySelector(this.config.selectors.trigger)?.setAttribute('aria-expanded', 'false');
	},

	closeAll() {
		document.querySelectorAll(`.${this.config.classes.open}`).forEach(dropdown => this.close(dropdown));
	},

	/**
	 * Met à jour libellé et option courante, sans toucher au select caché
	 */
	syncValue(dropdown, value, labelText = value) {
		const label = dropdown.querySelector(this.config.selectors.label);
		if (label) label.textContent = labelText;

		dropdown.querySelectorAll(this.config.selectors.item).forEach(item => {
			item.setAttribute('aria-current', String(item.dataset.productDropdownValue === String(value)));
		});
	},

	_selectValue(item) {
		const dropdown = item.closest(this.config.selectors.dropdown);
		const value = item.dataset.productDropdownValue;
		const hiddenSelect = dropdown.closest(this.config.selectors.pickerContainer)
			?.querySelector(this.config.selectors.hiddenSelect)
			?? dropdown.querySelector(this.config.selectors.ownInput);

		// Libellé avant le change : le sélecteur de devise envoie son formulaire au change
		this.syncValue(dropdown, value, item.dataset.productDropdownText ?? value);

		if (hiddenSelect && hiddenSelect.value !== value) {
			hiddenSelect.value = value;
			hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
		}

		this.close(dropdown);
		dropdown.querySelector(this.config.selectors.trigger)?.focus();
	},

	_handleKeydown(e, dropdown) {
		const trigger = dropdown.querySelector(this.config.selectors.trigger);
		// La valeur sélectionnée est masquée de la liste (aria-current="true")
		const items = Array.from(dropdown.querySelectorAll(this.config.selectors.item))
			.filter(item => item.getAttribute('aria-current') !== 'true');
		const index = items.indexOf(document.activeElement);
		const isOpen = dropdown.classList.contains(this.config.classes.open);

		switch (e.key) {
			case 'Escape':
				if (!isOpen) return;
				e.preventDefault();
				this.close(dropdown);
				trigger?.focus();
				break;
			case 'ArrowDown':
				e.preventDefault();
				if (!isOpen) this.open(dropdown);
				items[index < 0 ? 0 : (index + 1) % items.length]?.focus();
				break;
			case 'ArrowUp':
				if (index < 0) return;
				e.preventDefault();
				items[(index - 1 + items.length) % items.length]?.focus();
				break;
			case 'Home':
			case 'End':
				if (index < 0) return;
				e.preventDefault();
				items[e.key === 'Home' ? 0 : items.length - 1]?.focus();
				break;
		}
	},
};

window.ProductDropdownManager = ProductDropdownManager;

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => ProductDropdownManager.init());
} else {
	ProductDropdownManager.init();
}
